import { TextField } from "@mui/material";
import CustomTooltip from "@product-app/common/components/CustomTooltip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { FieldConfidence } from "../../hooks/useWorkflowStatus";
import { getColorHexCodeByColorName as token } from "../../tokens";

interface Props {
  label: string;
  field: FieldConfidence;
  editable?: boolean;
  onChange?: (value: string) => void;
}

/**
 * Renders one parsed-plan field with its confidence indicator. The label
 * + chrome use Tailwind + brand tokens; the editable text input itself
 * stays MUI per the ui-component-patterns rule ("MUI for styled
 * components: TextField, IconButton, Menu …").
 *
 * The three hex constants below are the resolved values of the
 * Highstreet Tailwind tokens primary-success-700 / primary-warning-700 /
 * primary-error-700 — they're applied via MUI sx (inline) because MUI's
 * sx API doesn't read Tailwind class names. Where the value drives a
 * className-only path, the Tailwind token is used directly.
 */
// Resolved from src/tokens.ts — the same module tailwind.config.ts reads.
const SUCCESS = token("primary-success-700");
const WARNING = token("primary-warning-700");
const ERROR = token("primary-error-700");

function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.8) return SUCCESS;
  if (confidence >= 0.5) return WARNING;
  return ERROR;
}

function getConfidenceLabel(confidence: number): string {
  if (confidence >= 0.8) return "High";
  if (confidence >= 0.5) return "Medium";
  return "Low";
}

export default function ConfidenceField({
  label,
  field,
  editable = true,
  onChange,
}: Props) {
  const color = getConfidenceColor(field.confidence);
  const displayValue = field.value != null ? String(field.value) : "";

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1">
        <span className="text-xs font-bold text-primary-gray-light-600">
          {label}
        </span>
        {/* ora's CustomTooltip, not a bare MUI Tooltip (story 28633). */}
        <CustomTooltip
          id={`confidence-field-${label.replace(/\W+/g, "-")}`}
          title={`Confidence: ${(field.confidence * 100).toFixed(0)}% (${getConfidenceLabel(field.confidence)}) — Source: ${field.source}`}
          placement="top"
          arrow
          isOnModal
        >
          {field.needs_am_input ? (
            <WarningAmberIcon sx={{ fontSize: 14, color: ERROR }} />
          ) : (
            <CheckCircleIcon sx={{ fontSize: 14, color }} />
          )}
        </CustomTooltip>
      </div>

      {editable ? (
        <TextField
          fullWidth
          size="small"
          value={displayValue}
          onChange={(e) => onChange?.(e.target.value)}
          error={field.needs_am_input}
          helperText={
            field.needs_am_input ? "Please verify this field" : undefined
          }
          sx={{
            "& .MuiOutlinedInput-root": {
              borderLeft: `3px solid ${color}`,
              fontSize: "0.875rem",
            },
            "& .MuiOutlinedInput-input": {
              padding: "0.4rem 0.625rem",
            },
            "& .MuiFormHelperText-root": {
              marginLeft: "0.25rem",
              fontSize: "0.6875rem",
            },
          }}
        />
      ) : (
        <div
          className="text-sm text-primary-gray-light-700 ps-2"
          style={{ borderLeft: `3px solid ${color}` }}
        >
          {displayValue || "—"}
        </div>
      )}
    </div>
  );
}
